// The SignInCard layout (design/kit/components/SignInCard) and the fields every sign-in card shares.
import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from '../shell/icons';

type SignInLayoutProps = {
  children: ReactNode;
  // One muted line under the card, as on the board.
  footnote?: string;
  toast?: ReactNode;
};

export function SignInLayout({ children, footnote, toast }: SignInLayoutProps) {
  return (
    <div className="lpage">
      <div className="lwrap">
        {children}
        {footnote && (
          <p className="m0 t12 muted" style={{ textAlign: 'center' }}>
            {footnote}
          </p>
        )}
      </div>
      {toast}
    </div>
  );
}

export function Brand() {
  return (
    <div className="lbrand">
      <span className="ss-logo ss-logo-lg" role="img" aria-label="Safeside Insurance"></span>
    </div>
  );
}

/** The card's title and one line under it, centered (the board's "Check your email"). */
export function CardTitle({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="col g4" style={{ textAlign: 'center' }}>
      <h1 id={id} className="dtitle">
        {title}
      </h1>
      <p className="m0 t13 muted">{children}</p>
    </div>
  );
}

/** The red error line (`errl`) from the Input card. */
export function ErrorLine({ text, center = false }: { text: string; center?: boolean }) {
  return (
    <p className="errl" role="alert" style={center ? { justifyContent: 'center' } : undefined}>
      <AlertCircleIcon />
      {text}
    </p>
  );
}

/** The amber line (`amb`) the board uses for account disabled and "Having issues signing in?". */
export function AmberLine({
  text,
  icon,
  role = 'status',
}: {
  text: string;
  icon: ReactNode;
  role?: 'status' | 'alert';
}) {
  return (
    <div className="amb" role={role}>
      {icon}
      <span>{text}</span>
    </div>
  );
}

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  inputRef?: Ref<HTMLInputElement>;
  autoFocus?: boolean;
  invalid?: boolean;
};

/** Password with show and hide (`pw` and `eye`). */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  inputRef,
  autoFocus,
  invalid,
}: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  return (
    <div className="lf">
      <label htmlFor={id}>{label}</label>
      <div className="pw">
        <input
          id={id}
          ref={inputRef}
          className="input"
          type={shown ? 'text' : 'password'}
          autoComplete={shown ? 'off' : autoComplete}
          autoFocus={autoFocus}
          maxLength={128}
          aria-invalid={invalid || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="eye"
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-controls={id}
          onClick={() => setShown((s) => !s)}
        >
          {shown ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}
